/* eslint-disable react/jsx-curly-brace-presence */
import React, { useState, Fragment, useContext } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { Button, FormControl } from '@material-ui/core';
import { ReactDatePicker, Note } from '@mummssoftware/common-ui';
import {
  formatLocDateMMDDYY,
  convertUtcToLocal,
} from '@mummssoftware/utils/formatters';
import ReactQuill from 'react-quill';
import defaults from '../../../translations/en.json';
import './style/style.css';
import { AppContext } from '../../App/AppContext';
import { DiscardModal, DiscardModalContext } from './DiscardModal';
import { modules, formats, RTF_BLANK_STRING } from '../../../constants';

import 'react-quill/dist/quill.snow.css';

const {
  Certify,
  Next,
  ApprovalHeaderText,
  PhysiciansNote,
  Record,
  Submit,
  SelectDate,
  DISCARD_TEXT,
} = defaults;
// todo common-ui
const ModalHeader = styled.div`
  display: flex;
  flex-direction: row;
`;

export const ButtonContainer = styled.div`
  text-align: right;
  margin-right: 10px;
  margin-bottom: 12px;
  margin-top: 12px;
`;

const DateHeader = styled.div`
  text-align: left;
  font-size: 0.85em;
  margin-bottom: 1px;
  margin-top: 3px;
`;

const DatePickerContainer = styled.div`
  width: 150px;
  text-align: right;
  margin-right: 10px;
  margin-bottom: 4px;
  margin-top: 1px;
`;

export const CalendarDiv = styled.div``;

const PpnBody = styled.div`
  margin-left: 30px;
  margin-right: 10px;
  margin-top: 5px;
  max-height: 350px;
  overflow: scroll;
  white-space: pre-wrap;
`;

const ApprovalHeader = styled.div`
  padding-top: 5px;
  padding-bottom: 5px;
`;

export const ApprovalHeaderTextContainer = styled.div`
  margin-left: 15px;
  display: block;
  font-size: 1.17em;
  font-weight: bold;
  text-decoration: underline;
`;

const TextAreaHeader = styled.div`
  display: block;
  font-size: 1.17em;
`;

export const ApprovalEditor = styled.div`
  margin: 1rem 0.5rem;
  white-space: pre-wrap;
  display: block;
  @media print {
    .page,
    .page-break {
      break-after: page;
    }
  }

  .ql-snow.ql-toolbar {
    display: block;
    background: #eaecec;
  }
  && .ql-editor {
    min-height: 8em;
    color: black;
    height: fit-content;
  }
  && .ql-container {
    background: #fefcfc;
    min-height: 15em;
    overflow: auto;
  }
`;

const buttonStyles = {
  marginLeft: '10px',
};

export const UnsignedApproval = React.forwardRef((props, ref) => {
  const {
    approval,
    approval: { requestorpersonfirstname, requestorpersonlastname },
    numOfApprovals,
    handleNext,
    handlePrev,
    handleApprove,
    ppn,
    pso,
    theme,
    isDoctorRoleGroup,
    index,
  } = props;
  const {
    palette: { background },
  } = theme;
  let { physiciannote } = approval;
  const { entityordertypeshortname, verbaldatetime } = approval;
  const { formatMessage } = useIntl();
  // approval api sends verbal back in utc.  needs to be converted to local
  const localShiftedVerbalDateTime = convertUtcToLocal(verbaldatetime);
  const formattedVerbalDate = formatLocDateMMDDYY(localShiftedVerbalDateTime);

  const { userNumber } = useContext(AppContext);

  // eslint-disable-next-line no-unused-vars
  const [disableApprove, setDisableApprove] = useState(false);
  const [physicianComment, setPhysicianComment] = useState('');
  const [recordedDate, setRecordedDate] = useState(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [discardOpen, openDiscardModal] = useState(false);

  const hasComment =
    physicianComment !== '' && physicianComment !== RTF_BLANK_STRING;
  const showNext = index !== numOfApprovals - 1;
  const showPrev = index !== 0;

  const clearInput = () => {
    setPhysicianComment('');
    setShowDatePicker(false);
    setDisableApprove(false);
    setRecordedDate(null);
  };

  const onNext = () => {
    handleNext();
    clearInput();
  };

  const onPrev = () => {
    handlePrev();
    clearInput();
  };

  const onSubmit = (canCertify) => {
    if (index > 0 && index === numOfApprovals - 1) {
      onPrev();
    } else {
      clearInput();
    }
    handleApprove(physicianComment, recordedDate, canCertify);
  };

  const discardAndSkip = () => {
    clearInput();
    if (showNext) {
      onNext();
    } else if (showPrev) {
      onPrev();
    }
  };

  // if no note is supplied on the workflow entity replace with "N/A"
  if (physiciannote === '') physiciannote = 'N/A';

  return (
    <Fragment ref={ref}>
      <ModalHeader style={{ backgroundColor: background.oddrow }}>
        {showDatePicker && (
          <DatePickerContainer>
            <FormControl>
              <DateHeader>{SelectDate}</DateHeader>
              <CalendarDiv>
                <ReactDatePicker
                  selectedDate={recordedDate}
                  onChange={(date) => {
                    setRecordedDate(date);
                    setDisableApprove(false);
                  }}
                  maximumDate={new Date()}
                  minimumDate={new Date(localShiftedVerbalDateTime)}
                />
              </CalendarDiv>
            </FormControl>
          </DatePickerContainer>
        )}
        <ButtonContainer>
          {isDoctorRoleGroup &&
          approval.physicianpersonnumber === userNumber ? (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              disabled={disableApprove}
              style={buttonStyles}
              onClick={() => {
                onSubmit(true);
              }}
            >
              {Certify}
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              disabled={disableApprove}
              style={buttonStyles}
              onClick={() => {
                if (recordedDate) {
                  onSubmit(false);
                } else {
                  setShowDatePicker(true);
                  setDisableApprove(true);
                }
              }}
            >
              {recordedDate ? Submit : Record}
            </Button>
          )}
          {showPrev && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              style={buttonStyles}
              onClick={onPrev}
            >
              Previous
            </Button>
          )}
          {showNext && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              style={buttonStyles}
              onClick={onNext}
            >
              {Next}
            </Button>
          )}
          {hasComment && (
            <DiscardModalContext.Provider
              value={{ open: discardOpen, setOpen: openDiscardModal }}
            >
              <DiscardModal onConfirm={discardAndSkip} />
              <Button
                variant="outlined"
                color="primary"
                size="small"
                style={buttonStyles}
                onClick={() => openDiscardModal(true)}
              >
                {DISCARD_TEXT}
              </Button>
            </DiscardModalContext.Provider>
          )}
        </ButtonContainer>
        <div
          style={{
            position: 'relative',
            alignSelf: 'center',
            border: '1px solid',
            borderRadius: '1vh',
            padding: '0.75vh',
          }}
        >
          {index + 1} of {numOfApprovals}
        </div>
      </ModalHeader>
      <ApprovalHeader style={{ backgroundColor: background.dark3 }}>
        <ApprovalHeaderTextContainer>
          {formatMessage(ApprovalHeaderText, {
            entityordertypeshortname,
            requestorpersonfirstname,
            requestorpersonlastname,
            formattedVerbalDate,
          })}
        </ApprovalHeaderTextContainer>
      </ApprovalHeader>
      <PpnBody id="cert-notes">
        <Note ppn={ppn} pso={pso} />
      </PpnBody>
      <TextAreaHeader>{PhysiciansNote}</TextAreaHeader>
      <ApprovalEditor>
        <ReactQuill
          theme="snow"
          onChange={(html) => setPhysicianComment(html)}
          value={physicianComment}
          modules={modules}
          formats={formats}
          placeholder="type something"
        />
      </ApprovalEditor>
    </Fragment>
  );
});
